import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import { catalogs } from 'store';
import {
    defaultVmDataDisk,
    environmentDetails,
    isConfigDiskFulfilled,
    isMandatoryTagsFullfilled,
    orderId,
    sspSubmitOrder,
    toggleSspModels,
    toggleSapPgtRequest,
    isSapSidValid,
    isValidProvisioningForm,
} from 'pages/provisioning/pgcs-vm/store';
import ProvisioningStyles from 'pages/provisioning/vm/style';
import { formOrderObjFromCart, formOrderObjFromOrderDetails, resetEnvironmentDetails, resetOrder, resetToggleSspModal } from './utils';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/pgcs-vm/ProvisioningHeader'));
const ApplicationDetails = React.lazy(() => import('pages/provisioning/pgcs-vm/ApplicationDetails'));
const SspForm = React.lazy(() => import('pages/provisioning/pgcs-vm/SspForm'));
const Tags = React.lazy(() => import('pages/provisioning/pgcs-vm/Tags'));
const AddedToCartConfirmModal = React.lazy(() => import('pages/provisioning/pgcs-vm/AddedToCartConfirmModal'));
const UpdatedToCartConfirmModal = React.lazy(() => import('pages/provisioning/pgcs-vm/UpdatedToCartConfirmModal'));
const ConfirmDD = React.lazy(() => import('pages/provisioning/pgcs-vm/ConfirmDD'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/pgcs-vm/ShowCostDetails'));

const PgcsVmProvisioningPage = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [cmpCatalogs] = useRecoilState(catalogs);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const [vmDataDisk, setVmDataDisk] = useRecoilState(defaultVmDataDisk);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetEnvDetails] = useRecoilState(environmentDetails);
    const [, setEnvDetails] = useRecoilState(environmentDetails);
    const [, resetIsMandatoryTagsFullfilled] = useRecoilState(isMandatoryTagsFullfilled);
    const [, resetIsConfigDiskFulfilled] = useRecoilState(isConfigDiskFulfilled);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const [, resetToggleSapPgtRequest] = useRecoilState(toggleSapPgtRequest);

    const [, resetSapSidValid] = useRecoilState(isSapSidValid);
    const [, setValidProvisioningForm] = useRecoilState(isValidProvisioningForm);

    const { osType, serverType, catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const axios = useAxios();
    async function getCosts() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);
        return data;
    }

    async function checkCatalog() {
        const isWindows = osType === 'Windows';
        const selector = isWindows ? 'azure_windows_vm_host' : 'azure_rhel_vm_host';
        const data = cmpCatalogs?.find((item) => item?.catalogDefinitionId === catalogCode);
        const costs = await getCosts();
        setVmDataDisk({
            ...vmDataDisk,
            diskSizeInGib: data?.provisioning?.requestPayloadDefinition?.extra_vars[selector]?.os_disk_size_gb,
        });

        if (cartItemId && operation && !catalogServiceId) {
            const { data: cartItem } = await axios.get(`${endPoints.cart.getCartItem}/${cartItemId}`);
            const orderObj = await formOrderObjFromCart(cartItem, osType, serverType, data?.images?.sku);
            const { envDetailsTemp, ...orderObjWithoutEnvTemp } = orderObj;
            setEnvDetails(envDetailsTemp);
            setOrder((previous) => ({
                ...previous,
                ...orderObjWithoutEnvTemp,
                sku: data?.images?.sku,
                emptyCost: costs,
            }));
        }
        if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axios.get(`${endPoints.order.orderDetails}/${cartItemId}`);
            const orderObj = await formOrderObjFromOrderDetails(catalogServiceId, orderDetails, osType, serverType, data?.images?.sku);
            const { envDetailsTemp, ...orderObjWithoutEnvTemp } = orderObj;
            setEnvDetails(envDetailsTemp);
            setOrder((previous) => ({
                ...previous,
                ...orderObjWithoutEnvTemp,
                sku: data?.images?.sku,
            }));
        } else {
            setOrder((previous) => ({
                ...previous,
                osType,
                sku: data?.images?.sku,
                // catalogOffer: data?.offer,
                serverType,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }
    const reset = () => {
        setOrder({ ...order, ...resetOrder });
        resetOrderId('');
        resetEnvDetails(resetEnvironmentDetails);
        resetIsMandatoryTagsFullfilled(false);
        resetToggleSspModels(resetToggleSspModal);
        resetIsConfigDiskFulfilled(false);
        resetToggleSapPgtRequest(false);
        resetSapSidValid(true);
        setValidProvisioningForm(false);
    };
    React.useEffect(() => {
        if (cmpCatalogs?.length && osType && serverType && catalogCode) {
            setLoading(true);
            reset();
            setVmDataDisk(undefined);
            checkCatalog();
        }
    }, [cmpCatalogs, osType, serverType, catalogCode]);

    /* eslint-disable arrow-body-style */
    React.useEffect(() => {
        return () => reset();
        // eslint-disable-next-line
    }, []);
    return (
        <>
            <Helmet>
                <title>VM Provisioning - DevX Infra</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <ApplicationDetails />
                        <Tags />
                        <AddedToCartConfirmModal />
                        <UpdatedToCartConfirmModal />
                        <ConfirmDD />
                        <ShowCostDetails />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default React.memo(PgcsVmProvisioningPage);
